import React, { useState, useEffect, useRef, useCallback, useReducer, FC } from 'react';

import { fabric } from 'fabric';
import {
  useCanvasEvents,
  useCanvasKeyboardEvents,
  useCanvasRotation,
  useCanvasSizeChange,
  useContextMenu,
  useSetImages,
  useUpdateImages,
  useCanvasSelectAll,
  useScaleBackImages
} from './Hooks';

import { reducer } from './Canvas.reducer';

import {
  extractFabricObjectMovementProperties,
  mapGroupPositionToObjectPostion,
  scaleBackImages,
  sendImagesToBack,
  bringImagesToFront
} from '../../../shared/Helpers';

type CanvasProps = {
  id: string;
  rawImages: any[];
  subsampleFactor: number;
  isStitched: boolean;
  onImagesLoaded: Function;
  selectAllImages: boolean;
  resetCanvas: boolean;
  isRotating: boolean;
  hideImages: boolean;
  doUndo: boolean;
  doRedo: boolean;
  onImagesHidden: () => void;
  onShowHideImageModal: () => void;
  onImageChange: (scaledImages: ReturnType<typeof scaleBackImages>) => void;
  onObjectSelected: (isAnyActive: boolean, areAllActive: boolean) => void;
  setStitchingCanvas?: (canvas: fabric.Canvas) => void;
};

let numberOfKeyEvents = 0;

const Canvas: FC<CanvasProps> = ({
  id,
  rawImages,
  subsampleFactor,
  isStitched,
  onImagesLoaded,
  resetCanvas,
  selectAllImages,
  isRotating,
  hideImages,
  onImagesHidden,
  onShowHideImageModal,
  onImageChange,
  onObjectSelected,
  doRedo,
  doUndo,
  setStitchingCanvas
}) => {
  const [canvasEl, setCanvasEl] = useState<Nullable<fabric.Canvas>>(null);

  const canvasElRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const newCanvas = new fabric.Canvas(canvasElRef.current!, {
      uniScaleKey: 'none'
    });
    (newCanvas as any).uniformScaling = true;

    setCanvasEl(newCanvas);
    if (setStitchingCanvas) setStitchingCanvas(newCanvas);
  }, []);

  const [canvasState, dispatch] = useReducer(reducer, {
    fabricImages: [],
    presentFabricImages: [],
    rawImages,
    pastFabricImages: [],
    futureFabricImages: [],
    isStitched,
    leftScale: 0,
    topScale: 0,
    centringLeftScale: 0,
    canvasHeight: 960,
    canvasWidth: 1500,
    zoom: 1,
    reRender: false
  });

  const { fabricImages, presentFabricImages, zoom, leftScale, centringLeftScale, topScale, pastFabricImages, futureFabricImages, reRender } =
    canvasState;

  useEffect(() => {
    dispatch({
      type: 'UPDATE_REDUCER',
      rawImages,
      isStitched,
      subsampleFactor
    });
  }, [isStitched, rawImages, resetCanvas]);

  const canUndo = pastFabricImages.length > 0;
  const canRedo = futureFabricImages.length > 0;

  const undoCallback = useCallback(() => {
    if (canUndo) {
      dispatch({ type: 'UNDO' });
    }
  }, [canUndo]);

  const redoCallback = useCallback(() => {
    if (canRedo) {
      dispatch({ type: 'REDO' });
    }
  }, [canRedo]);

  useEffect(() => {
    if (doUndo) {
      undoCallback();
    }
  }, [doUndo, undoCallback]);

  useEffect(() => {
    if (doRedo) {
      redoCallback();
    }
  }, [doRedo, redoCallback]);

  const dispatchToHistory = useCallback((updatedObjects: any[], isMultiSelection: boolean, skipSmallMovements: boolean, reRender = false) => {
    if (skipSmallMovements) {
      numberOfKeyEvents++;
    }

    const canDispatch = isMultiSelection || !skipSmallMovements || numberOfKeyEvents % 5 === 1;

    if (canDispatch) {
      dispatch({
        type: 'ADD_TO_HISTORY',
        fabricImages: updatedObjects,
        reRender: reRender
      });
    }
  }, []);

  const mapActiveObjectsAndAddToHistory = useCallback(
    (activeObjects: fabric.Object[], skipSmallMovements: boolean) => {
      if (activeObjects && activeObjects.length < 1) {
        return;
      }
      const isMultiSelection = activeObjects.length > 1;
      const updatedObjects = isMultiSelection
        ? activeObjects.map(mapGroupPositionToObjectPostion)
        : [extractFabricObjectMovementProperties(activeObjects[0] as any)];

      dispatchToHistory(updatedObjects, isMultiSelection, skipSmallMovements);
    },
    [dispatchToHistory]
  );

  const sizeChangedHandler = useCallback((canvasWidth, canvasHeight) => {
    dispatch({ type: 'SIZE_CHANGED', canvasHeight, canvasWidth });
  }, []);

  useScaleBackImages(centringLeftScale, leftScale, topScale, onImageChange, presentFabricImages, subsampleFactor);

  useCanvasEvents(canvasEl, mapActiveObjectsAndAddToHistory, onObjectSelected, null);

  useCanvasKeyboardEvents(canvasEl, mapActiveObjectsAndAddToHistory, undoCallback, redoCallback);

  useSetImages(canvasEl, fabricImages, onImagesLoaded);
  useCanvasRotation(canvasEl, isRotating, mapActiveObjectsAndAddToHistory);
  useCanvasSizeChange(canvasEl, sizeChangedHandler, 'canvas-container');
  useUpdateImages(canvasEl, presentFabricImages, reRender);
  useCanvasSelectAll(canvasEl, selectAllImages);

  useEffect(() => {
    if (canvasEl) {
      canvasEl.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvasEl.setZoom(1 / zoom);
    }
  }, [canvasEl, zoom]);

  const updateImagesVisiblity = useCallback(
    (objects: fabric.Object[]) => {
      const isMultiSelection = objects.length > 1;
      const updatedObjects = objects.map(extractFabricObjectMovementProperties as any);

      dispatchToHistory(updatedObjects, isMultiSelection, false);

      onImagesHidden();
    },
    [onImagesHidden, dispatchToHistory]
  );

  const [contextMenuConfig] = useContextMenu(canvasEl, hideImages, updateImagesVisiblity);

  const onSendToBack = () => {
    const imagesAdjustedByRanking = sendImagesToBack(canvasEl!, contextMenuConfig.names);
    dispatchToHistory(imagesAdjustedByRanking, true, true, true);
  };

  const onBringToFront = () => {
    const imagesAdjustedByRanking = bringImagesToFront(canvasEl!, contextMenuConfig.names);
    dispatchToHistory(imagesAdjustedByRanking, true, true, true);
  };

  return (
    <>
      <div id='canvas-container' style={{ height: '85vh', overflow: 'hidden' }}>
        <canvas id={id} ref={canvasElRef} />
      </div>
      {contextMenuConfig.show && (
        <div className='menu' style={{ left: contextMenuConfig.left, top: contextMenuConfig.top }}>
          <ul className='menu-options'>
            <li className='menu-option' onClick={onShowHideImageModal}>
              Hide
            </li>
            <li className='menu-option' onClick={onBringToFront}>
              Bring to front
            </li>
            <li className='menu-option' onClick={onSendToBack}>
              Send to back
            </li>
          </ul>
        </div>
      )}
    </>
  );
};

export default Canvas;
